// Screens (plan step 3.4; D15): every screen's loads, TTID and TTFD (TTFD only where the app
// reports it, spec 8.2) and the hangs that happened on it; one screen's histogram, days,
// releases and slowest loads, with the sessions behind the slow ones.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { isApiError } from '../api/client'
import {
  framesQuery,
  hangsQuery,
  issuesQuery,
  screenQuery,
  screensListQuery,
  screensQuery,
  sessionQuery,
} from '../api/queries'
import type { Percentiles, ScreenSummary, Screens } from '../api/types'
import {
  axisTicks,
  btnClass,
  EmptyNote,
  HovRow,
  LK,
  Pill,
  Section,
  SortTh,
  Strip,
  type StripItem,
  Table,
  Waterfall,
  type WaterfallSpan,
} from '../components/d'
import { LineChart } from '../components/LineChart'
import {
  ApproxFigure,
  Counted,
  Delta,
  Histogram,
  LatencyText,
  NoApp,
  PerfHead,
  perfMeta,
  ReleaseLink,
  releaseMarker,
  usePerfReleases,
  usePerfScope,
  withRelease,
} from '../components/Perf'
import { ErrorState, Loading } from '../components/states'
import { type Translator, useI18n } from '../i18n'
import { compact, duration, durationAxis, percentSpaced, perMinute, shortId } from '../lib/format'
import { twoHumps } from '../lib/histogram'
import { issueTitle } from '../lib/issues'
import { latency, ttidAt } from '../lib/latency'
import { parseTime, type SessionModel, sessionModel, type TimelineEvent } from '../lib/session'
import { change, perThousand, rate1k, splitMs } from './Performance'
import { useProjectParams } from './params'
import { carried, type ListSearch, type SCREEN_SORTS, type ScreenSearch } from './search'

const ms = (i18n: Translator, p: Percentiles | null | undefined, key: 'p50_ms' | 'p90_ms') =>
  p ? duration(i18n, p[key]) : '—'

type ScreenSort = (typeof SCREEN_SORTS)[number]
/** The columns sorted here, over the listed rows; the others are the API's sorts. */
type LocalSort = 'screen' | 'ttid_p50' | 'slow' | 'frozen' | 'hangs'

const SORT_META = {
  loads: 'perf.screens.sortMeta.loads',
  ttid_p90: 'perf.screens.sortMeta.ttid',
  ttfd_p90: 'perf.screens.sortMeta.ttfd',
} as const

const frameKey = (appId: string, screen: string) => `${appId} ${screen}`

/** The screen with the slowest TTFD p90, or TTID p90 where no screen reports TTFD. */
export function slowestScreen(rows: ScreenSummary[]): {
  row: ScreenSummary | undefined
  ttfd: boolean
} {
  const ttfd = rows.some((r) => r.ttfd)
  // TTID from the spans, else approximately from the rollups (D114, APS-445).
  const p90 = (r: ScreenSummary | undefined) =>
    (ttfd ? r?.ttfd?.p90_ms : ttidAt(r, 'p90_ms')?.ms) ?? -1
  let row: ScreenSummary | undefined
  for (const r of rows) if (p90(r) > p90(row)) row = r
  return { row, ttfd }
}

export function ScreensPage() {
  const i18n = useI18n()
  const { t, number, rich } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ListSearch
  const { scope, app, appsPending } = usePerfScope()
  const { current, previous } = usePerfReleases(scope)
  const [local, setLocal] = useState<LocalSort | undefined>()
  const sort = (search.sort as ScreenSort | undefined) ?? 'loads'
  const enabled = !!scope.app_id
  const screens = useQuery({ ...screensListQuery(projectId, scope, sort), enabled })
  // Slow and frozen frames per screen, joined on the app and the screen's name.
  const frames = useQuery({ ...framesQuery(projectId, scope, 'minutes'), enabled })
  // The slowest screen's TTFD in the compared release and the one before it (D103 #14).
  const stripScope = scope.release ? scope : withRelease(scope, current)
  const now = useQuery({
    ...screensQuery(projectId, stripScope, 'ttfd_p90', 100),
    enabled: enabled && !!stripScope.release && !!previous,
  })
  const before = useQuery({
    ...screensQuery(projectId, withRelease(scope, previous), 'ttfd_p90', 100),
    enabled: enabled && !!previous,
  })
  const params = { orgId, projectId }
  const keep = { ...carried(search), app: scope.app_id }
  const title = t('perf.screens.title')
  const page = t('perf.nav.screens')

  if (appsPending)
    return (
      <main id="main" className="pg pf-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} />
        <NoApp />
      </main>
    )

  const android = app.platform === 'android'
  const data = screens.data
  const rows = data?.screens ?? []
  const frameOf = new Map(
    (frames.data?.screens ?? []).map((f) => [frameKey(f.app_id, f.screen), f]),
  )
  const hangRate = (r: ScreenSummary) => perThousand(r.hangs, r.loads)
  const meta = perfMeta(i18n, app, scope, [
    data ? t('perf.screens.metaCount', { count: rows.length }) : null,
    'app.screen.load',
  ])

  // the strip's slowest screen: in the compared release when there is one before it
  const compared = previous && now.data ? now.data : rows
  const slow = slowestScreen(compared)
  const pick = (r: ScreenSummary | undefined) =>
    slow.ttfd ? r?.ttfd?.p90_ms : ttidAt(r, 'p90_ms')?.ms
  const slowWas = slow.row
    ? pick(before.data?.find((r) => r.app_id === slow.row?.app_id && r.screen === slow.row.screen))
    : undefined
  const slowNow = pick(slow.row)
  const [slowV, slowU] = splitMs(i18n, slowNow)
  const topHang = rows.reduce<ScreenSummary | undefined>(
    (a, r) => (r.hangs > (a?.hangs ?? 0) ? r : a),
    undefined,
  )
  const worstHangs = rows.reduce<ScreenSummary | undefined>(
    (a, r) => ((hangRate(r) ?? 0) > ((a && hangRate(a)) ?? 0) ? r : a),
    undefined,
  )

  const toScreen = (r: Pick<ScreenSummary, 'app_id' | 'screen'>) => ({
    to: '/o/$orgId/p/$projectId/performance/screens/detail' as const,
    params,
    search: { ...keep, app: r.app_id, screen: r.screen },
  })

  const strip = (d: Screens): StripItem[] => [
    {
      dt: t('perf.screens.loads'),
      dd: compact(i18n, d.totals.loads),
      d: t('perf.screens.loadsLine', { count: rows.length }),
    },
    ...(slow.row
      ? [
          {
            dt: slow.ttfd ? t('perf.screens.slowestTtfd') : t('perf.screens.slowestTtid'),
            dd:
              !slow.ttfd && ttidAt(slow.row, 'p90_ms')?.approx ? (
                <ApproxFigure>{slowV}</ApproxFigure>
              ) : (
                slowV
              ),
            small: slowU,
            d: (
              <>
                {previous ? (
                  <>
                    <Delta value={change(slowNow, slowWas)} format={(v) => duration(i18n, v)} /> ·{' '}
                  </>
                ) : null}
                <span translate="no">{slow.row.screen}</span>
              </>
            ),
            link: ({ className, children }) => (
              <Link className={className} {...toScreen(slow.row as ScreenSummary)}>
                {children}
              </Link>
            ),
          } satisfies StripItem,
        ]
      : []),
    {
      dt: android ? t('perf.screens.anrs') : t('perf.screens.hangs'),
      dd: rate1k(i18n, perThousand(d.totals.hangs, d.totals.loads)),
      small: t('perf.screens.perLoads'),
      d:
        topHang && d.totals.hangs > 0 ? (
          <span translate="no">
            {t('perf.screens.hangsOn', {
              n: number(topHang.hangs),
              total: number(d.totals.hangs),
              screen: topHang.screen,
            })}
          </span>
        ) : (
          t('perf.screens.noHangs')
        ),
      link: ({ className, children }) => (
        <Link
          className={className}
          to="/o/$orgId/p/$projectId/performance/hangs"
          params={params}
          search={keep}
        >
          {children}
        </Link>
      ),
    },
  ]

  const setSort = (s: ScreenSort) => {
    setLocal(undefined)
    return navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, sort: s === 'loads' ? undefined : s }),
    })
  }
  const head = (s: ScreenSort, label: string) => (
    <SortTh on={!local && sort === s} onSort={() => setSort(s)} className="num">
      {label}
    </SortTh>
  )
  const localHead = (s: LocalSort, label: string, num = true) => (
    <SortTh on={local === s} onSort={() => setLocal(s)} {...(num ? { className: 'num' } : {})}>
      {label}
    </SortTh>
  )
  const desc =
    (f: (r: ScreenSummary) => number | null | undefined) => (a: ScreenSummary, b: ScreenSummary) =>
      (f(b) ?? -1) - (f(a) ?? -1)
  const frame = (r: ScreenSummary) => frameOf.get(frameKey(r.app_id, r.screen))
  const sorted =
    local === 'screen'
      ? [...rows].sort((a, b) => a.screen.localeCompare(b.screen))
      : local === 'ttid_p50'
        ? [...rows].sort(desc((r) => ttidAt(r, 'p50_ms')?.ms))
        : local === 'slow'
          ? [...rows].sort(desc((r) => frame(r)?.slow_share))
          : local === 'frozen'
            ? [...rows].sort(desc((r) => frame(r)?.frozen_per_minute))
            : local === 'hangs'
              ? [...rows].sort(desc(hangRate))
              : rows

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead title={title} page={page} meta={meta} />
      {screens.isPending ? (
        <Loading />
      ) : screens.isError ? (
        <ErrorState error={screens.error} retry={() => screens.refetch()} />
      ) : !data || rows.length === 0 ? (
        <EmptyNote title={t('perf.screens.none')}>{rich('perf.screens.noneBody')}</EmptyNote>
      ) : (
        <>
          <Strip items={strip(data)} translate="no" />
          <Section title={t('perf.screens.all')} meta={local ? undefined : t(SORT_META[sort])}>
            <Table label={t('perf.screens.title')} rowKeys className="pf-tbl">
              <thead>
                <tr>
                  {localHead('screen', t('vitals.col.screen'), false)}
                  {head('loads', t('vitals.col.loads'))}
                  {localHead('ttid_p50', t('vitals.col.ttidP50'))}
                  {head('ttid_p90', t('vitals.col.ttidP90'))}
                  {head('ttfd_p90', t('vitals.col.ttfdP90'))}
                  {localHead('slow', t('perf.screens.col.slow'))}
                  {localHead('frozen', t('perf.screens.col.frozen'))}
                  {localHead(
                    'hangs',
                    android ? t('perf.screens.col.anrs') : t('perf.screens.col.hangs'),
                  )}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const f = frame(r)
                  const ttfd = ms(i18n, r.ttfd, 'p90_ms')
                  const hangs = rate1k(i18n, hangRate(r))
                  return (
                    <HovRow key={`${r.app_id} ${r.screen}`} onOpen={() => navigate(toScreen(r))}>
                      <td className="mono anywhere" translate="no">
                        <Link {...toScreen(r)}>{r.screen}</Link>
                      </td>
                      <td className="num">{compact(i18n, r.loads)}</td>
                      <td className="num">
                        <LatencyText v={ttidAt(r, 'p50_ms')} />
                      </td>
                      <td className="num">
                        <LatencyText v={ttidAt(r, 'p90_ms')} />
                      </td>
                      <td className="num">
                        {slow.ttfd &&
                        r.app_id === slow.row?.app_id &&
                        r.screen === slow.row.screen ? (
                          <Pill tone="warn">{ttfd}</Pill>
                        ) : (
                          ttfd
                        )}
                      </td>
                      <td className="num">
                        {f?.slow_share === null || f?.slow_share === undefined
                          ? '—'
                          : percentSpaced(i18n, f.slow_share, 1)}
                      </td>
                      <td className="num">
                        {f?.frozen_per_minute === null || f?.frozen_per_minute === undefined
                          ? '—'
                          : perMinute(i18n, f.frozen_per_minute)}
                      </td>
                      <td
                        className="num"
                        title={t(android ? 'perf.screens.anrCount' : 'perf.screens.hangCount', {
                          count: r.hangs,
                        })}
                      >
                        {r === worstHangs && r.hangs > 0 ? <Pill tone="warn">{hangs}</Pill> : hangs}
                      </td>
                    </HovRow>
                  )
                })}
              </tbody>
            </Table>
            {rows.some((r) => ttidAt(r, 'p90_ms')?.approx) ? (
              <p className="pf-note">{t('perf.approx.note')}</p>
            ) : null}
            <p className="pf-note">{t('perf.screens.lede')}</p>
            <p className="pf-note">{t('perf.screens.foot')}</p>
          </Section>
        </>
      )}
    </main>
  )
}

/** A sample load's place in its session: the load span closest to the sample's time. */
export interface LoadSample {
  session_id: string
  at: string
  ttid_ms: number
  ttfd_ms: number | null
}

/**
 * What happened during one load: the load itself, then the requests, spans, hangs and crashes
 * of its session that overlap it, on the load's own clock. Undefined when the session's timeline
 * has no load of this screen near the sample.
 */
export function loadWaterfall(
  model: SessionModel,
  screen: string,
  sample: LoadSample,
): { load: TimelineEvent; end: number; spans: TimelineEvent[] } | undefined {
  const at = parseTime(sample.at) - model.startedAt
  let load: TimelineEvent | undefined
  for (const e of model.events)
    if (
      e.signal === 'screen' &&
      e.sub === screen &&
      (!load || Math.abs(e.at - at) < Math.abs(load.at - at))
    )
      load = e
  if (!load || Math.abs(load.at - at) > 1000) return undefined
  const end = load.at + Math.max(load.duration, sample.ttid_ms, sample.ttfd_ms ?? 0)
  const from = load.at
  const spans = model.events.filter(
    (e) => e !== load && DURING.has(e.signal) && e.at <= end && e.at + e.duration >= from,
  )
  return { load, end, spans }
}

const DURING = new Set(['network', 'span', 'start', 'hang', 'crash', 'error', 'exit'])

/** Sample rows a table shows, as the mock: the full list is one link away. */
const SAMPLES = 5

/** The slowest load nearest the screen's p90: the waterfall's representative load. */
export function sampleLoad<T extends LoadSample>(
  slowest: T[],
  p90: number | undefined,
): T | undefined {
  if (p90 === undefined) return slowest[0]
  const value = (s: T) => s.ttfd_ms ?? s.ttid_ms
  let best: T | undefined
  for (const s of slowest)
    if (!best || Math.abs(value(s) - p90) < Math.abs(value(best) - p90)) best = s
  return best
}

export function ScreenPage() {
  const i18n = useI18n()
  const { t, number, date, relative } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ScreenSearch
  const { scope, app, appsPending } = usePerfScope()
  const { list, current, previous } = usePerfReleases(scope)
  const name = search.screen ?? ''
  const appId = scope.app_id ?? ''
  const enabled = !!appId && !!name
  const detail = useQuery({ ...screenQuery(projectId, appId, name, scope, true), enabled })
  // Every release in the window, for the per-release table and the deltas (D103 #14).
  const everyRelease = withRelease(scope, undefined)
  const all = useQuery({
    ...screenQuery(projectId, appId, name, everyRelease),
    enabled: enabled && !!scope.release,
  })
  const hangs = useQuery({ ...hangsQuery(projectId, scope), enabled })
  const hangsByRelease = useQuery({
    ...hangsQuery(projectId, everyRelease, false, 'release'),
    enabled,
  })
  const frames = useQuery({ ...framesQuery(projectId, scope, 'minutes'), enabled })
  const android = app?.platform === 'android'
  const issue = useQuery({
    ...issuesQuery(projectId, {
      app_id: appId,
      screen: name,
      kind: android ? 'anr' : 'hang',
      sort: 'occurrences',
      days: scope.days,
      limit: 1,
    }),
    enabled,
  })
  const d = detail.data
  const withTtfd = !!d?.ttfd
  const p90 = d ? (withTtfd ? d.ttfd?.p90_ms : d.ttid?.p90_ms) : undefined
  const sample = d ? sampleLoad(d.slowest, p90) : undefined
  const session = useQuery({
    ...sessionQuery(projectId, sample?.session_id ?? ''),
    enabled: !!sample,
  })
  // The sample's session is past its detail retention, or erased (APS-399).
  const sampleGone = session.isError && isApiError(session.error, 404)
  const params = { orgId, projectId }
  const keep = { ...carried(search), app: appId }
  const page = (
    <>
      <Link
        to="/o/$orgId/p/$projectId/performance/screens"
        params={params}
        search={carried(search)}
      >
        {t('perf.nav.screens')}
      </Link>
    </>
  )
  const title = <span translate="no">{name || t('perf.screens.title')}</span>

  if (appsPending || (enabled && detail.isPending))
    return (
      <main id="main" className="pg pf-pg">
        <Loading />
      </main>
    )
  if (!app)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} mono />
        <NoApp />
      </main>
    )
  if (detail.isError)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} mono />
        <ErrorState error={detail.error} retry={() => detail.refetch()} />
      </main>
    )
  const meta = perfMeta(i18n, app, scope, ['app.screen.load'])
  if (!d || d.loads === 0)
    return (
      <main id="main" className="pg pf-pg">
        <PerfHead title={title} page={page} meta={meta} mono />
        <EmptyNote title={t('perf.screen.none')}>
          <Link
            to="/o/$orgId/p/$projectId/performance/screens"
            params={params}
            search={carried(search)}
          >
            {t('perf.screen.all')}
          </Link>
        </EmptyNote>
      </main>
    )

  const fmt = (v: number) => duration(i18n, v)
  const rels = (all.data ?? d).releases
  const relOf = (r: string | undefined) => rels.find((x) => x.release === r)
  const value = (r: { ttid: Percentiles | null; ttfd: Percentiles | null } | undefined) =>
    (withTtfd ? r?.ttfd : r?.ttid)?.p90_ms
  const now = relOf(current)
  const was = relOf(previous)
  const hangRow = hangs.data?.screens.find((x) => x.app_id === d.app_id && x.screen === d.screen)
  const hangCount = hangRow ? (android ? hangRow.anrs : hangRow.hangs) : 0
  const frameRow = frames.data?.screens.find((x) => x.app_id === d.app_id && x.screen === d.screen)
  const topIssue = issue.data?.issues[0]
  const ttid = ttidAt(d, 'p90_ms')
  const [ttidV, ttidU] = splitMs(i18n, ttid?.ms)
  // No load's span was released: the daily chart, the histogram and the samples, which read spans
  // only, have nothing; its TTID is the rollups' (D114, APS-445).
  const spanless = !!ttid?.approx
  const [ttfdV, ttfdU] = splitMs(i18n, d.ttfd?.p90_ms)
  const threshold = p90 !== undefined ? Math.round(p90) : undefined

  const strip: StripItem[] = [
    {
      dt: t('vitals.col.loads'),
      dd: compact(i18n, d.loads),
      d:
        d.exact_loads > 0 ? (
          <Counted exact={d.exact_loads} estimated={d.estimated_loads} />
        ) : (
          t('perf.screen.perDay', { n: compact(i18n, Math.round(d.loads / scope.days)) })
        ),
    },
    {
      dt: t('vitals.col.ttidP90'),
      dd: spanless ? <ApproxFigure>{ttidV}</ApproxFigure> : ttidV,
      small: ttidU,
      d: t('perf.card.p50', { value: latency(i18n, ttidAt(d, 'p50_ms')) }),
    },
    {
      dt: t('vitals.col.ttfdP90'),
      dd: ttfdV,
      small: ttfdU,
      d: d.ttfd ? (
        <>
          {previous ? (
            <>
              <Delta value={change(value(now), value(was))} format={fmt} /> ·{' '}
            </>
          ) : null}
          {t('perf.card.p50', { value: ms(i18n, d.ttfd, 'p50_ms') })}
        </>
      ) : (
        t('perf.screen.noTtfd')
      ),
    },
    {
      dt: android ? t('perf.screens.anrs') : t('perf.screens.hangs'),
      dd: rate1k(i18n, perThousand(hangCount, d.loads)),
      small: t('perf.screens.perLoads'),
      d:
        hangCount > 0 ? (
          <span translate="no">
            {[
              t(android ? 'perf.screens.anrCount' : 'perf.screens.hangCount', {
                count: hangCount,
              }),
              topIssue ? issueTitle(topIssue) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : (
          t('perf.screens.noHangs')
        ),
      link: ({ className, children }) =>
        topIssue ? (
          <Link
            className={className}
            to="/o/$orgId/p/$projectId/issues/$issueId"
            params={{ ...params, issueId: topIssue.id }}
            search={carried(search)}
          >
            {children}
          </Link>
        ) : (
          <Link
            className={className}
            to="/o/$orgId/p/$projectId/performance/hangs"
            params={params}
            search={keep}
          >
            {children}
          </Link>
        ),
    },
    ...(frameRow && frameRow.slow_share !== null
      ? [
          {
            dt: t('perf.screens.col.slow'),
            dd: number(frameRow.slow_share * 100, { maximumFractionDigits: 1 }),
            small: '%',
            d: t('perf.screen.framesLine', {
              slow:
                frameRow.slow_per_minute === null ? '—' : perMinute(i18n, frameRow.slow_per_minute),
              frozen:
                frameRow.frozen_per_minute === null
                  ? '—'
                  : perMinute(i18n, frameRow.frozen_per_minute),
            }),
            link: ({ className, children }) => (
              <Link
                className={className}
                to="/o/$orgId/p/$projectId/performance/frames"
                params={params}
                search={keep}
              >
                {children}
              </Link>
            ),
          } satisfies StripItem,
        ]
      : []),
  ]

  const day = (x: string) =>
    date(`${x}T00:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const days = d.days.map((x) => x.day)
  // The release marker's legend item: "5.2 (812) shipped Sep 29" (APS-392).
  const shippedAt = list.find((r) => r.release === current)?.released_at
  const shipped =
    current && shippedAt
      ? t('perf.screen.shipped', { release: current, date: i18n.date(shippedAt) })
      : undefined
  const hist = withTtfd
    ? { data: d.ttfd_histogram ?? d.histogram, fine: d.ttfd_fine_histogram, p: d.ttfd }
    : { data: d.histogram, fine: d.fine_histogram, p: d.ttid }
  // Two humps in the distribution, split at the dip between them (Mosaic, APS-392).
  const modes = hist.fine ? twoHumps(hist.fine) : undefined

  // the waterfall of the sample load, from its session's timeline
  const model = session.data ? sessionModel(session.data) : undefined
  const wf = model && sample ? loadWaterfall(model, d.screen, sample) : undefined

  const rowsByRelease = [...rels].sort(
    (a, b) =>
      (list.findIndex((r) => r.release === a.release) + 1 || 1e9) -
      (list.findIndex((r) => r.release === b.release) + 1 || 1e9),
  )
  const hangsOf = (release: string) => {
    const h = hangsByRelease.data?.screens.find(
      (x) => x.app_id === d.app_id && x.screen === d.screen && x.release === release,
    )
    return h ? (android ? h.anrs : h.hangs) : 0
  }

  return (
    <main id="main" className="pg pf-pg">
      <PerfHead
        title={title}
        page={page}
        meta={meta}
        mono
        actions={
          threshold !== undefined ? (
            <Link
              className={btnClass()}
              to="/o/$orgId/p/$projectId/sessions"
              params={params}
              search={{
                app: d.app_id,
                screen: d.screen,
                ...(withTtfd ? { min_ttfd: threshold } : { min_ttid: threshold }),
              }}
            >
              {t('perf.screen.slowSessionsOver', { value: fmt(threshold) })}
            </Link>
          ) : undefined
        }
      />
      <Strip items={strip} translate="no" />
      <div className="grid2">
        <Section
          title={withTtfd ? t('perf.screen.dailyTtfd') : t('perf.screen.dailyTtid')}
          meta={t('perf.frames.dailyMeta', {
            days: scope.days,
            release: scope.release ?? t('perf.meta.allReleases'),
          })}
        >
          <div className="pf-chart">
            {spanless && !withTtfd ? (
              <EmptyNote title={t('perf.approx.spans')}>{t('perf.approx.spansBody')}</EmptyNote>
            ) : d.days.every((x) => x.ttid_p90_ms === null) ? (
              <EmptyNote title={t('perf.trend.empty')} />
            ) : (
              <LineChart
                label={t(withTtfd ? 'perf.screen.dailyTtfdLabel' : 'perf.screen.dailyLabel', {
                  days: scope.days,
                })}
                accent
                name={withTtfd ? 'TTFD p90' : 'TTID p90'}
                points={d.days.map((x) => ({
                  label: day(x.day),
                  value: withTtfd ? x.ttfd_p90_ms : x.ttid_p90_ms,
                }))}
                more={
                  withTtfd
                    ? [{ name: 'TTID p90', values: d.days.map((x) => x.ttid_p90_ms) }]
                    : undefined
                }
                marker={releaseMarker(list, current, days)}
                // The mock's legend, always: the lines and the release that shipped (APS-392).
                legend
                markerLegend={shipped}
                // One unit on the axis (written on its top tick), the full duration at the end.
                fromZero
                format={durationAxis(
                  i18n,
                  Math.max(...d.days.flatMap((x) => [x.ttid_p90_ms ?? 0, x.ttfd_p90_ms ?? 0])),
                )}
                lastLabel={(p) => (p.value === null ? '' : fmt(p.value))}
                timeAxis
                xName={t('chart.day')}
              />
            )}
          </div>
        </Section>
        <Section
          title={t('perf.screen.histogram')}
          meta={
            spanless && !withTtfd
              ? undefined
              : t(withTtfd ? 'perf.screen.histogramTtfd' : 'perf.screen.histogramLabel', {
                  loads: hist.p?.count ?? d.loads,
                })
          }
        >
          {spanless && !withTtfd ? (
            <EmptyNote title={t('perf.approx.spans')} />
          ) : (
            <Histogram
              data={hist.data}
              fine={hist.fine}
              fit
              hiFromMs={modes?.valleyMs}
              p50={hist.p?.p50_ms}
              p90={hist.p?.p90_ms}
              countName={(count) => t('chart.loads', { count })}
              label={t(withTtfd ? 'perf.screen.histogramTtfd' : 'perf.screen.histogramLabel', {
                loads: hist.p?.count ?? d.loads,
              })}
              // A bar opens the sessions that loaded this screen at least that slowly (APS-577),
              // as the slower half's link below does.
              onBucket={(ms) =>
                navigate({
                  to: '/o/$orgId/p/$projectId/sessions',
                  params,
                  search: {
                    app: d.app_id,
                    screen: d.screen,
                    ...(withTtfd ? { min_ttfd: ms } : { min_ttid: ms }),
                  },
                })
              }
              bucketHint={t('perf.screen.openBucket')}
            />
          )}
          {modes ? (
            // Two humps: the fast loads, and the slow ones a click from their sessions (APS-392).
            <ul className="rl pf-modes">
              <li>
                <span>{t('perf.screen.modeUnder', { value: fmt(modes.valleyMs) })}</span>
                <span>{percentSpaced(i18n, modes.under, 0)}</span>
              </li>
              <li>
                <span>{t('perf.screen.modeOver', { value: fmt(modes.valleyMs) })}</span>
                <Link
                  className="ul"
                  to="/o/$orgId/p/$projectId/sessions"
                  params={params}
                  search={{
                    app: d.app_id,
                    screen: d.screen,
                    ...(withTtfd ? { min_ttfd: modes.valleyMs } : { min_ttid: modes.valleyMs }),
                  }}
                  // Its name starts with what it shows (WCAG 2.5.3, axe label-in-name, APS-488).
                  aria-label={t('perf.screen.modeOverLink', {
                    share: percentSpaced(i18n, modes.over, 0),
                    value: fmt(modes.valleyMs),
                  })}
                >
                  {percentSpaced(i18n, modes.over, 0)}
                </Link>
              </li>
            </ul>
          ) : null}
        </Section>
      </div>
      {sample ? (
        <Section
          title={t('perf.screen.during')}
          meta={t('perf.screen.duringMeta', {
            value: fmt(sample.ttfd_ms ?? sample.ttid_ms),
          })}
          right={
            // A sample that is gone has no session to open (Mosaic, APS-399).
            sampleGone ? undefined : (
              <Link
                className={btnClass('ghost', 'sm')}
                to="/o/$orgId/p/$projectId/sessions/$sessionId"
                params={{ ...params, sessionId: sample.session_id }}
                search={{ app: d.app_id }}
              >
                {t('perf.screen.openSession')}
              </Link>
            )
          }
        >
          {session.isPending ? (
            <Loading />
          ) : sampleGone ? (
            // The sample is past its detail retention, or erased: nothing to show, nothing broke
            // (Mosaic, APS-399). In the console's empty-note style, sans and dim.
            <p className="se-p dim">{t('perf.screen.sampleGone')}</p>
          ) : session.isError ? (
            <ErrorState error={session.error} retry={() => session.refetch()} />
          ) : !wf ? (
            <EmptyNote title={t('perf.screen.noTimeline')} />
          ) : (
            <LoadWaterfall
              wf={wf}
              screen={d.screen}
              ttid={sample.ttid_ms}
              ttfd={sample.ttfd_ms}
              appId={d.app_id}
            />
          )}
        </Section>
      ) : null}
      <Section
        title={t('perf.screen.slowest')}
        right={
          <Link
            className={btnClass('ghost', 'sm')}
            to="/o/$orgId/p/$projectId/sessions"
            params={params}
            search={{ app: d.app_id, screen: d.screen }}
          >
            {t('perf.screen.allSessions')}
          </Link>
        }
      >
        {d.slowest.length === 0 ? (
          <EmptyNote title={t(spanless ? 'perf.approx.spans' : 'perf.endpoint.noSamples')} />
        ) : (
          <Table
            label={t('table.of', { name: t('perf.screen.slowest') })}
            rowKeys
            className="pf-tbl"
          >
            <thead>
              <tr>
                <th scope="col">{t('sessions.col.session')}</th>
                <th scope="col">{t('sessions.col.started')}</th>
                <th scope="col" className="num" translate="no">
                  TTID
                </th>
                <th scope="col" className="num" translate="no">
                  TTFD
                </th>
                <th scope="col">{t('releases.col.release')}</th>
                <th scope="col">{t('sessions.col.device')}</th>
              </tr>
            </thead>
            <tbody>
              {d.slowest.slice(0, SAMPLES).map((s) => {
                const to = {
                  to: '/o/$orgId/p/$projectId/sessions/$sessionId' as const,
                  params: { ...params, sessionId: s.session_id },
                  search: { app: d.app_id },
                }
                return (
                  <HovRow key={`${s.session_id} ${s.at}`} onOpen={() => navigate(to)}>
                    <td className="mono">
                      <Link {...to} translate="no">
                        {shortId(s.session_id)}
                      </Link>
                    </td>
                    <td>{relative(s.at)}</td>
                    <td className="num">{fmt(s.ttid_ms)}</td>
                    <td className="num">{s.ttfd_ms === null ? '—' : fmt(s.ttfd_ms)}</td>
                    <td translate="no">
                      <ReleaseLink release={s.release} />
                    </td>
                    <td translate="no">
                      {[s.device_model, s.os_version].filter(Boolean).join(' · ')}
                    </td>
                  </HovRow>
                )
              })}
            </tbody>
          </Table>
        )}
      </Section>
      <Section
        title={t('perf.screen.releases')}
        meta={withTtfd ? t('perf.screen.releasesTtfd') : t('perf.screen.releasesTtid')}
      >
        {rowsByRelease.length === 0 ? (
          <EmptyNote title={t('perf.card.none')} />
        ) : (
          <Table label={t('table.of', { name: t('perf.screen.releases') })} className="pf-tbl">
            <thead>
              <tr>
                <th scope="col">{t('releases.col.release')}</th>
                <th scope="col" className="num">
                  {t('vitals.col.loads')}
                </th>
                <th scope="col" className="num">
                  {t('vitals.col.ttidP90')}
                </th>
                <th scope="col" className="num" translate="no">
                  TTFD p50
                </th>
                <th scope="col" className="num">
                  {t('vitals.col.ttfdP90')}
                </th>
                <th scope="col" className="num">
                  {android ? t('perf.screens.col.anrs') : t('perf.screens.col.hangs')}
                </th>
                <th scope="col" className="num">
                  {t('perf.col.change')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsByRelease.map((r, i) => {
                const at = list.findIndex((x) => x.release === r.release)
                const before = at >= 0 ? relOf(list[at + 1]?.release) : rowsByRelease[i + 1]
                const loads = r.loads
                return (
                  <tr key={r.release}>
                    <td translate="no">
                      <ReleaseLink release={r.release} />
                    </td>
                    <td className="num">{compact(i18n, loads)}</td>
                    <td className="num">
                      <LatencyText v={ttidAt(r, 'p90_ms')} />
                    </td>
                    <td className="num">{ms(i18n, r.ttfd, 'p50_ms')}</td>
                    <td className="num">{ms(i18n, r.ttfd, 'p90_ms')}</td>
                    <td className="num">{rate1k(i18n, perThousand(hangsOf(r.release), loads))}</td>
                    <td className="num">
                      <Delta value={change(value(r), value(before))} format={fmt} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Section>
    </main>
  )
}

/** One load and what overlapped it in its session, on the load's own clock. */
function LoadWaterfall({
  wf,
  screen,
  ttid,
  ttfd,
  appId,
}: {
  wf: NonNullable<ReturnType<typeof loadWaterfall>>
  screen: string
  ttid: number
  ttfd: number | null
  appId: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const from = wf.load.at
  // The axis covers the load and what runs past it, up to half the load again.
  const load = wf.end - from
  const ticks = axisTicks(
    Math.max(load, ...wf.spans.map((e) => Math.min(e.at + e.duration - from, load * 1.5))),
  )
  const axisEnd = from + (ticks.at(-1) ?? load)
  const fmt = (v: number) => duration(i18n, v)
  const root: WaterfallSpan = {
    key: 'load',
    name: 'app.screen.load',
    sub: ttfd !== null ? t('perf.screen.loadTtfd', { screen }) : screen,
    start: 0,
    duration: wf.end - from,
    bar: 'root',
    value: fmt(wf.end - from),
  }
  const spans: WaterfallSpan[] = wf.spans.map((e) => {
    const host = e.attributes['server.address']
    const tone = e.signal === 'hang' ? 'slow' : e.signal === 'crash' ? 'crash' : undefined
    return {
      key: e.id,
      name: e.name,
      sub:
        e.signal === 'network' ? [host, e.value].filter(Boolean).join(' · ') : e.sub || undefined,
      start: Math.max(0, e.at - from),
      duration: Math.max(0, Math.min(e.at + e.duration, axisEnd) - Math.max(e.at, from)),
      bar: tone ?? (e.signal === 'network' ? 'http' : undefined),
      value:
        e.signal === 'hang' ? (
          <Pill tone="warn">{fmt(e.duration)}</Pill>
        ) : e.signal === 'crash' ? (
          <span className="c-crash">{t('perf.screen.crash')}</span>
        ) : e.tone === 'crash' ? (
          <span className="c-crash">{fmt(e.duration)}</span>
        ) : (
          fmt(e.duration)
        ),
      links: e.traceId ? (
        <Link
          className={LK}
          to="/o/$orgId/p/$projectId/traces/$traceId"
          params={{ orgId, projectId, traceId: e.traceId }}
          search={{ app: appId }}
        >
          {t('perf.screen.traceTag')}
        </Link>
      ) : undefined,
    }
  })
  return (
    <>
      <Waterfall
        variant="load"
        i18n={i18n}
        label={t('perf.screen.duringLabel', { screen })}
        items={[root, ...spans]}
        ticks={ticks}
        marks={[
          { at: ttid, label: `TTID ${fmt(ttid)}` },
          ...(ttfd !== null ? [{ at: ttfd, label: `TTFD ${fmt(ttfd)}` }] : []),
        ]}
      />
      {spans.length === 0 ? <p className="pf-note">{t('perf.screen.nothingDuring')}</p> : null}
    </>
  )
}
